import { ApolloCache, Reference } from '@apollo/client'
import * as Sentry from '@sentry/react'

import {
  AcademicPlanningSettingsDocument,
  AcademicPlanningSettingsQuery,
  AddPlanMutation,
  AlertLevel,
  AlertType,
  AuditCourse,
  AuditReportDocument,
  AuditReportQuery,
  BoardAlertFragment,
  BoardAlertFragmentDoc,
  BoardCommentFragment,
  BoardCommentFragmentDoc,
  BoardGroupFragment,
  BoardGroupFragmentDoc,
  BoardItemFragment,
  BoardItemFragmentDoc,
  BoardPlanFragment,
  BoardPlanFragmentDoc,
  Career,
  CopyEmbeddedPlanMutation,
  CopyPlanMutation,
  CourseAvailability,
  DeletePlanItemMutation,
  MovePlanItemMutation,
  OwnerPlanFragment,
  PlanDocument,
  PlanItem,
  PlanItemDetailsFragment,
  PlanQuery,
  PlansByOwnerDocument,
  PlansByOwnerQuery,
  UpdatePlanItemMutation
} from '../generated/graphql'
import { addItemToList, changeItemSequence, removeItemFromList } from './utils'
import { filterSelfReferencingRequisite } from './requisiteUtils'

// Adding 'object' to the list of cache types because that seems to be what is returned from the subcription
export const updateOwnerPlansCache = (
  cache: ApolloCache<CopyPlanMutation | CopyEmbeddedPlanMutation | AddPlanMutation | object>,
  ownerAppUserId: number | undefined,
  newValue: OwnerPlanFragment | undefined
) => {
  // Update the right cache for the proper ownerAppUserId
  if (!newValue) {
    return
  }

  const existingPlans = cache.readQuery<PlansByOwnerQuery>({
    query: PlansByOwnerDocument,
    variables: { ownerAppUserId }
  })

  let newPlans: Array<OwnerPlanFragment> = []
  if (existingPlans !== null && existingPlans.plans !== null) {
    // ApplyTemplate scenario: the plan is from template and primary, in which case set all other plans to not be primary
    if (newValue.isFromTemplate && newValue.isPrimary) {
      newPlans = existingPlans.plans.map((p) => ({
        ...p,
        isPrimary: false
      }))
    } else {
      newPlans = existingPlans.plans
    }
  }

  // The same plan can arrive twice (one-shot query and subscription after an apply); keep one copy
  newPlans = newPlans.filter((plan) => plan.id !== newValue.id).concat(newValue)

  cache.writeQuery({
    query: PlansByOwnerDocument,
    data: { plans: newPlans },
    variables: { ownerAppUserId }
  })

  // Make sure the primary plan reference is pointed to the new plan.
  // Scope the update to the matching ownerAppUserId — the ROOT_QUERY may hold
  // multiple primaryPlan entries (e.g. an advisor viewing several students),
  // and without narrowing we would overwrite all of them. The regex anchor
  // `[,}]` prevents numeric-prefix collisions (e.g. 9615 must not match 96150).
  if (newValue.isPrimary) {
    const ownerArgPattern = new RegExp(`"ownerAppUserId":${ownerAppUserId}[,}]`)
    cache.modify({
      id: 'ROOT_QUERY',
      fields: {
        primaryPlan(existing, { toReference, storeFieldName }) {
          if (!ownerArgPattern.test(storeFieldName)) {
            return existing
          }
          return toReference({ __typename: 'Plan', id: newValue.id })
        }
      }
    })
  }
}

/** Get a flatten list of all Course Items (NOT including Placeholders) from the plan */
function getAllCourseItems<T>(cache: ApolloCache<T>, planId: string): Array<BoardItemFragment> {
  const planCacheId = `Plan:${planId}`
  const cachePlan = cache.readFragment<BoardPlanFragment>({
    id: planCacheId,
    fragment: BoardPlanFragmentDoc,
    fragmentName: 'boardPlan'
  })

  if (!cachePlan) {
    return []
  }
  const allCourseItems = cachePlan.groups
    .map((g) => g.items)
    .reduce((a, b) => a.concat(b), [])
    .filter((i) => i.subjectId && i.courseNumber)

  return allCourseItems
}

function detectAndAddDuplicateCourseAlerts<T>(
  cache: ApolloCache<T>,
  item: BoardItemFragment | null | undefined,
  planId: string
) {
  if (item && item.courseNumber && item.subjectId) {
    // find any duplicate courses and apply the DUPLICATE_COURSE alert
    const allCourseItems = getAllCourseItems(cache, planId)
    const dupeCourses = allCourseItems.filter(
      (c) => c.subjectId === item.subjectId && c.courseNumber === item.courseNumber
    )

    if (dupeCourses.length > 1) {
      const itemIds = dupeCourses
        .map((i) => parseInt(i.id, 10))
        // get unique values
        .filter((d, pos, arr) => arr.map((mapd) => mapd).indexOf(d) === pos)
        .sort((a: number, b: number) => a - b)
      const concatIds = itemIds.map((itemId) => `ITEM_${itemId}`).join('|')
      const alertKey = `${AlertType.DuplicateCourse}-${concatIds}`

      if (dupeCourses.length > 2) {
        // find any previous DUPLICATE_COURSE alerts (i.e. adding a 3rd duplicate)
        // and remove them because the alertKeys are different
        const existingCourse = dupeCourses.find((c) => c.id !== item.id)

        if (existingCourse) {
          cache.modify({
            id: 'ROOT_QUERY',
            fields: {
              planAlerts(existingAlerts: Reference[], { readField }) {
                const alerts = existingAlerts || []
                const prevAlertRef = alerts.find(
                  (ref) =>
                    AlertType.DuplicateCourse === readField('alertType', ref) &&
                    existingCourse.id === readField('itemId', ref)
                )
                const prevAlertKey = readField('alertKey', prevAlertRef)
                return alerts.filter((ref) => prevAlertKey !== readField('alertKey', ref))
              }
            }
          })
        }
      }

      // apply the DUPLICATE_COURSE alert to each related item
      dupeCourses.forEach((curItem) => {
        const newAlert: BoardAlertFragment = {
          __typename: 'PlanAlert',
          alertKey,
          alertType: AlertType.DuplicateCourse,
          level: AlertLevel.Warning,
          groupId: curItem.groupId || '',
          itemId: curItem.id
        }

        addPlanAlertToCache(cache, newAlert, curItem)
      })
    }
  }
}

/**
 * Add Plan Alert to overall alerts cache unless the alert already exists.
 * @param cache The Apollo Cache
 * @param alert The alert to add.
 * @param item The item to add the alert to.
 */
function addPlanAlertToCache<T>(
  cache: ApolloCache<T>,
  alert: BoardAlertFragment,
  item: BoardItemFragment
) {
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      planAlerts(existingAlerts: Reference[], { readField, storeFieldName }) {
        const alerts = existingAlerts || []
        // NOTE: This method gets executed for each planAlerts query cache,
        // ie: each time the planAlerts are retrieved by planId or itemId
        // (like when the planItemDetailsModal is opened)
        // Example storeFieldNames:
        //    planAlerts({"planId":"2"})
        //    planAlerts({"itemId":"102","planId":"2"})

        // Only update the cache for the overall plan or for this specific item, otherwise it will add this cached alert to unrelated items
        if (
          storeFieldName.includes(`"itemId":"${item.id}"`) ||
          (storeFieldName.includes(`"planId":"${item.planId}"`) &&
            !storeFieldName.includes('itemId'))
        ) {
          // Quick safety check - if the new alert is already
          // present in the cache, we don't need to add it again.
          if (
            alerts.some(
              (ref) =>
                readField('alertKey', ref) === alert.alertKey &&
                readField('itemId', ref) === alert.itemId
            )
          ) {
            return alerts
          }

          const alertRef = cache.writeFragment({
            data: alert,
            fragment: BoardAlertFragmentDoc
          })

          // filter out any previous alerts of the same type and itemID.
          // this may be ghost alert from the same course that has already been deleted
          // These ghost alerts may be happening for a couple reasons:
          // 1. By design through Apollo, they are being kept from being evicting earlier.
          // 2. A bug in Apollo's cache system
          // 3. We are doing too many "cache.modify"'s in one action and Apollo cannot evict removed objects that quickly
          const previousAlerts = alerts.filter(
            (ref) =>
              !(
                readField('itemId', ref) === alert.itemId &&
                readField('alertType', ref) === alert.alertType
              )
          )
          return [...previousAlerts, alertRef]
        }

        return alerts
      }
    }
  })
}

/**
 * Check item and add Requisite Alert if conditions are met.
 * Conditions: RequisiteDescriptionLong is not empty.
 * @param cache The Apollo Cache
 * @param item Item to added Requisite Alert to if conditions are met
 */
function detectAndAddRequisiteAlert<T>(
  cache: ApolloCache<T>,
  item:
    | (Pick<PlanItem, 'requisiteDescription' | 'requisiteDescriptionLong'> & BoardItemFragment)
    | null
    | undefined
) {
  // PLAN-935: if the only requisite data is a self-reference (PeopleSoft
  // sometimes lists a course as its own prereq), treat the item as having no
  // requisite and skip the alert.
  const effectiveShortRequisite = item
    ? filterSelfReferencingRequisite(item.requisiteDescription, item.subjectId, item.courseNumber)
    : null
  const effectiveLongRequisite = item
    ? filterSelfReferencingRequisite(
        item.requisiteDescriptionLong,
        item.subjectId,
        item.courseNumber
      )
    : null

  if (
    item &&
    item.groupId &&
    item.courseNumber &&
    item.subjectId &&
    (effectiveShortRequisite || effectiveLongRequisite) &&
    item.showRequisiteAlert
  ) {
    // apply the REQUISITE alert to the item
    const newAlert: BoardAlertFragment = {
      __typename: 'PlanAlert',
      alertKey: `${AlertType.Requisite}-ITEM_${item.id}-${item.subjectId}_${item.courseNumber}`,
      alertType: AlertType.Requisite,
      level: AlertLevel.Warning,
      groupId: item.groupId,
      itemId: item.id
    }
    addPlanAlertToCache(cache, newAlert, item)
  }
}

/**
 * Check item and add Unavailable Alert if conditions are met.
 * Conditions: availability is set to UNAVAILABLE
 * @param cache The Apollo Cache
 * @param item Item to added Requisite Alert to if conditions are met
 */
function detectAndAddUnavailableAlert<T>(
  cache: ApolloCache<T>,
  item: BoardItemFragment | null | undefined
) {
  if (
    item &&
    item.groupId &&
    item.courseNumber &&
    item.subjectId &&
    item.availability === CourseAvailability.Unavailable
  ) {
    // apply the UNAVAILABLE alert to the item
    const newAlert: BoardAlertFragment = {
      __typename: 'PlanAlert',
      alertKey: `${AlertType.Unavailable}-ITEM_${item.id}_${item?.groupId}-${item.subjectId}_${item.courseNumber}`,
      alertType: AlertType.Unavailable,
      level: AlertLevel.Warning,
      groupId: item.groupId,
      itemId: item.id
    }
    addPlanAlertToCache(cache, newAlert, item)
  }
}

/**
 * Check item and add Inactive Alert if conditions are met.
 * Condition: isActive is falsey
 * @param cache The Apollo Cache
 * @param item Item to add Inactive Alert to if conditions are met
 */
function detectAndAddInactiveCourseAlert<T>(
  cache: ApolloCache<T>,
  item: BoardItemFragment | null | undefined
) {
  if (item && item.groupId && item.courseNumber && item.subjectId && !item.isActive) {
    // apply the Inactive alert to the item
    const newAlert: BoardAlertFragment = {
      __typename: 'PlanAlert',
      alertKey: `${AlertType.InactiveCourse}-ITEM_${item.id}`,
      alertType: AlertType.InactiveCourse,
      level: AlertLevel.Warning,
      groupId: item.groupId,
      itemId: item.id
    }
    addPlanAlertToCache(cache, newAlert, item)
  }
}

/**
 * Retrieve all Audit Courses from the cached Audit Report.
 * Audit Report is loaded/updated in cache whenever user navigates to PlanBoard or refreshes the audit.
 *
 * @param cache current ApolloCache
 * @param studentAppUserId App User ID of the student whose audit courses are being retrieved
 * @returns array of AuditCourse objects from the cached audit report
 */
function getAuditCourses<T>(cache: ApolloCache<T>, studentAppUserId: number): AuditCourse[] {
  const auditReport = cache.readQuery<AuditReportQuery>({
    query: AuditReportDocument,
    variables: { studentAppUserId }
  })

  const courses: AuditCourse[] = []
  auditReport?.auditReport.careers.forEach((career: Career) => {
    career.requirementGroups?.forEach((reqGroup) => {
      reqGroup.requirements.forEach((req) => {
        req.courses?.forEach((course) => {
          courses.push({
            ...course,
            requirementGroupExternalId: reqGroup.externalId as string,
            requirementExternalId: req.externalId as string,
            requirementLineTitle: null,
            requirementTitle: req.title
          })
        })

        req.requirementLines?.forEach((reqLine) => {
          reqLine.courses?.forEach((course) => {
            courses.push({
              ...course,
              requirementGroupExternalId: reqGroup.externalId as string,
              requirementLineTitle: reqLine.title,
              requirementExternalId: req.externalId as string,
              requirementTitle: req.title
            })
          })
        })
      })
    })
    career.fallThroughCourses?.forEach((course) => {
      if (course) courses.push(course)
    })
    career.overTheLimitCourses?.forEach((course) => {
      if (course) courses.push(course)
    })
    career.insufficientCourses?.forEach((course) => {
      if (course) courses.push(course)
    })
  })

  return courses
}

/**
 * Match a PlanItem to a list of AuditCourse objects.
 * Filter by subjectId and courseNumber first,
 * then filter by equivalentCourseGroupId if no matches are found.
 *
 * @param courses Array of AuditCourse objects
 * @param item PlanItem to match to a list of AuditCourse objects
 * @returns Array of AuditCourse objects that match the PlanItem
 */
export function getMatchingAuditCourses(
  courses: AuditCourse[],
  item: BoardItemFragment,
  enableCrossListingUsedCourses?: boolean
): AuditCourse[] {
  // Filter audit courses by subject and courseNumber
  let matchingAuditCourses = courses.filter(
    (c) => c.subjectId === item.subjectId && c.courseNumber === item.courseNumber
  )
  // Only look for equivalent audit courses if we can't find a match by subjectId and courseNumber
  if (!matchingAuditCourses?.length && item.equivalentCourseGroupId) {
    matchingAuditCourses = courses.filter(
      (c) => c.equivalentCourseGroupId === item.equivalentCourseGroupId
    )
  }
  // If no matches are found, try with the externalCourseId if enableCrossListingUsedCourses is true
  if (!matchingAuditCourses?.length && enableCrossListingUsedCourses) {
    matchingAuditCourses = courses.filter(
      (c) => c.externalCourseId?.split('-')[0] === item.externalCourseId?.split('-')[0]
    )
  }
  return matchingAuditCourses
}

/**
 * Find AuditCourse that is used to meet a requirement.
 * Note that PeopleSoft and DegreeWorks represent unused courses differently.
 *
 * @param courses Array of Audit Courses
 * @param notUsedRequirementGroups List of requirement groups that represent unused courses
 * @param notUsedRequirements List of requirements that represent unused courses
 * @returns Audit Course used to meet a requirement
 */
export function findUsedAuditCourse(
  courses: AuditCourse[],
  notUsedRequirementGroups?: string[],
  notUsedRequirements?: string[],
  failedGrades?: string[]
): AuditCourse | undefined {
  return courses.find(
    (c) =>
      (c.usedToMeetRequirement || c.courseTakenInReq) &&
      !c.inProgress &&
      !c.enrolled &&
      !c.withdrawn &&
      // PeopleSoft may represent unused courses as a special Requirement or RequirementGroup.
      // See notUsedRequirementGroups and notUsedRequirements config for details.
      !notUsedRequirementGroups?.includes(c.requirementGroupExternalId) &&
      !notUsedRequirements?.includes(c.requirementExternalId) &&
      // DegreeWorks represents unused courses using the overTheLimit and fallThrough flags.
      !c.overTheLimit &&
      !(c.fallThrough && c.fallThroughCode === 'ECA-OVERFLOW') &&
      // Check for failed grades
      !failedGrades?.includes(c.gradeReceived)
  )
}

/**
 * Find AuditCourse that is not used to meet a requirement.
 * Note that PeopleSoft and DegreeWorks represent unused courses differently.
 *
 * @param courses Array of AuditCourse objects
 * @param notUsedRequirementGroups List of requirement groups that represent unused courses
 * @param notUsedRequirements List of requirements that represent unused courses
 * @returns Audit Course not used to meet a requirement
 */
export function findUnusedAuditCourse(
  courses: AuditCourse[],
  notUsedRequirementGroups?: string[],
  notUsedRequirements?: string[],
  failedGrades?: string[]
): AuditCourse | undefined {
  return courses.find(
    (c) =>
      (!c.inProgress &&
        // PeopleSoft may represent unused courses as a special Requirement or RequirementGroup.
        // See notUsedRequirementGroups and notUsedRequirements config for details.
        (notUsedRequirementGroups?.includes(c.requirementGroupExternalId) ||
          notUsedRequirements?.includes(c.requirementExternalId) ||
          // DegreeWorks represents unused courses using the overTheLimit and fallThrough flags.
          c.overTheLimit ||
          (c.fallThrough && c.fallThroughCode === 'ECA-OVERFLOW'))) ||
      // Check for failed grades
      (failedGrades && failedGrades?.includes(c.gradeReceived))
  )
}

/**
 * Add an alert to the cache if the item matches an audit course that was used to meet a requirement.
 *
 * @param cache Apollo Client Cache
 * @param item Item to check for ItemMeetsRequirement alert
 * @param courses Array of Audit Courses
 */
function detectAndAddUsedToMeetRequirementAlert<T>(
  cache: ApolloCache<T>,
  item: BoardItemFragment | null | undefined,
  courses: AuditCourse[],
  enableCrossListingUsedCourses?: boolean
) {
  // Item must have courseNumber and subjectId to match an audit course
  if (item?.courseNumber && item?.subjectId) {
    const matchingAuditCourses = getMatchingAuditCourses(
      courses,
      item,
      enableCrossListingUsedCourses
    )

    // Now find out if any of the matching records are usedToMeetRequirement and add the alert
    if (matchingAuditCourses?.length) {
      const settings = cache.readQuery<AcademicPlanningSettingsQuery>({
        query: AcademicPlanningSettingsDocument
      })
      const usedAuditCourse = findUsedAuditCourse(
        matchingAuditCourses,
        settings?.academicPlanningSettings.notUsedRequirementGroups,
        settings?.academicPlanningSettings.notUsedRequirements,
        settings?.academicPlanningSettings.failedGrades
      )

      if (usedAuditCourse) {
        const newAlert: BoardAlertFragment = {
          __typename: 'PlanAlert',
          alertType: AlertType.ItemMeetsRequirement,
          alertKey: `${AlertType.ItemMeetsRequirement}-ITEM_${item.id}_${item.groupId}`,
          level: AlertLevel.Success,
          groupId: item.groupId,
          itemId: item.id
        }
        addPlanAlertToCache(cache, newAlert, item)

        // Update the plan item as well, set usedToMeetRequirement = true
        // this is for the use case when audit is generated and we don't re-retrieve the items
        const cacheItem = cache.readFragment<BoardItemFragment>({
          id: `PlanItem:${item.id}`,
          fragment: BoardItemFragmentDoc,
          fragmentName: 'boardItem'
        })

        cache.writeFragment({
          id: `PlanItem:${item.id}`,
          fragment: BoardItemFragmentDoc,
          fragmentName: 'boardItem',
          data: {
            ...cacheItem,
            usedToMeetRequirement: true
          }
        })
      }
    }
  }
}

/**
 * Add an alert to the cache if the item matches an audit course that was not used to meet any requirements.
 *
 * @param cache Apollo Client Cache
 * @param item Item to check for ItemDoesNotMeetRequirement alert
 * @param courses Array of Audit Courses
 */
function detectAndAddNotUsedToMeetRequirementAlert<T>(
  cache: ApolloCache<T>,
  item: BoardItemFragment | null | undefined,
  auditCourses: AuditCourse[]
) {
  // Item must have courseNumber and subjectId to match an audit course
  if (item && item.courseNumber && item.subjectId) {
    const matchingAuditCourses = getMatchingAuditCourses(auditCourses, item)

    if (matchingAuditCourses?.length) {
      const settings = cache.readQuery<AcademicPlanningSettingsQuery>({
        query: AcademicPlanningSettingsDocument
      })
      const notUsedRequirementGroups = settings?.academicPlanningSettings.notUsedRequirementGroups
      const notUsedRequirements = settings?.academicPlanningSettings.notUsedRequirements
      const failedGrades = settings?.academicPlanningSettings.failedGrades
      const notUsedAuditCourse = findUnusedAuditCourse(
        matchingAuditCourses,
        notUsedRequirementGroups,
        notUsedRequirements,
        failedGrades
      )
      // Suppress not-meet alert if any career shows used/in-progress/enrolled (PLAN-953).
      const courseUsedOrInProgressInAnyCareer = matchingAuditCourses.some(
        (c) =>
          c.inProgress ||
          c.enrolled ||
          (!failedGrades?.includes(c.gradeReceived) &&
            (c.usedToMeetRequirement || c.courseTakenInReq) &&
            !c.withdrawn &&
            !notUsedRequirementGroups?.includes(c.requirementGroupExternalId) &&
            !notUsedRequirements?.includes(c.requirementExternalId))
      )

      if (notUsedAuditCourse && !courseUsedOrInProgressInAnyCareer) {
        const newAlert: BoardAlertFragment = {
          __typename: 'PlanAlert',
          alertType: AlertType.ItemDoesNotMeetRequirement,
          alertKey: `${AlertType.ItemDoesNotMeetRequirement}-ITEM_${item.id}_${item.groupId}`,
          level: AlertLevel.Warning,
          groupId: item.groupId,
          itemId: item.id
        }
        addPlanAlertToCache(cache, newAlert, item)
      }
    }
  }
}

/**
 * Add an alert to the cache if the item matches an audit course that is enrolled.
 * The method also updates the item enrollmentStatus even if the alert is not being added,
 * for cases when an audit course does not match with the item or the audit course is not registered anymore.
 *
 * @param cache Apollo Client Cache
 * @param item Item to check for EnrolledCourse alert / EnrollmentStatus
 * @param courses Array of Audit Courses
 */
function detectAndAddEnrolledCourseAlert<T>(
  cache: ApolloCache<T>,
  item: BoardItemFragment | null | undefined,
  courses: AuditCourse[]
) {
  if (item?.courseNumber && item?.subjectId) {
    const matchingAuditCourses = getMatchingAuditCourses(courses, item)
    const enrolledAuditCourse = matchingAuditCourses?.find((c) => !!c.enrolled)

    if (enrolledAuditCourse?.enrolled) {
      const newAlert: BoardAlertFragment = {
        __typename: 'PlanAlert',
        alertType: AlertType.EnrolledCourse,
        alertKey: `${AlertType.EnrolledCourse}-ITEM_${item.id}_${item.groupId}`,
        level: AlertLevel.Success,
        groupId: item.groupId,
        itemId: item.id
      }
      addPlanAlertToCache(cache, newAlert, item)
    }

    // Update the plan item as well, set inProgress and populate enrollmentStatus
    const cacheItem = cache.readFragment<BoardItemFragment>({
      id: `PlanItem:${item.id}`,
      fragment: BoardItemFragmentDoc,
      fragmentName: 'boardItem'
    })

    let enrollmentStatus = null
    if (enrolledAuditCourse?.enrolled) {
      enrollmentStatus = {
        enrolled: enrolledAuditCourse.enrolled,
        termCode: enrolledAuditCourse.termCode,
        termDescription: enrolledAuditCourse.termDescription
      }
    }

    cache.writeFragment({
      id: `PlanItem:${item.id}`,
      fragment: BoardItemFragmentDoc,
      fragmentName: 'boardItem',
      data: {
        ...cacheItem,
        inProgress: !enrolledAuditCourse?.enrolled ? cacheItem?.inProgress : false,
        enrollmentStatus
      }
    })
  }
}

/**
 * Add an alert to the cache if the item matches an audit course that is in progress.
 *
 * @param cache Apollo Client Cache
 * @param item Item to check for InProgressItem alert
 * @param courses Array of Audit Courses
 */
function detectAndAddInProgressCourseAlert<T>(
  cache: ApolloCache<T>,
  item: BoardItemFragment | null | undefined,
  courses: AuditCourse[]
) {
  // Item must have courseNumber and subjectId to match an audit course
  if (item?.courseNumber && item?.subjectId) {
    const matchingAuditCourses = getMatchingAuditCourses(courses, item)

    // Now find out if any of the matching records are inProgress and add the alert
    if (matchingAuditCourses?.length) {
      const settings = cache.readQuery<AcademicPlanningSettingsQuery>({
        query: AcademicPlanningSettingsDocument
      })
      const inProgressAuditCourse = matchingAuditCourses.find(
        (c) =>
          c.inProgress &&
          !settings?.academicPlanningSettings.notUsedRequirementGroups?.includes(
            c.requirementGroupExternalId
          ) &&
          !settings?.academicPlanningSettings.notUsedRequirements?.includes(c.requirementExternalId)
      )

      if (inProgressAuditCourse && !inProgressAuditCourse.enrolled) {
        const newAlert: BoardAlertFragment = {
          __typename: 'PlanAlert',
          alertType: AlertType.InProgressCourse,
          alertKey: `${AlertType.InProgressCourse}-ITEM_${item.id}_${item.groupId}`,
          level: AlertLevel.Success,
          groupId: item.groupId,
          itemId: item.id
        }
        addPlanAlertToCache(cache, newAlert, item)

        // Update the plan item as well, set inProgress = true
        // this is for the use case when audit is generated and we don't re-retrieve the items
        const cacheItem = cache.readFragment<BoardItemFragment>({
          id: `PlanItem:${item.id}`,
          fragment: BoardItemFragmentDoc,
          fragmentName: 'boardItem'
        })

        cache.writeFragment({
          id: `PlanItem:${item.id}`,
          fragment: BoardItemFragmentDoc,
          fragmentName: 'boardItem',
          data: {
            ...cacheItem,
            inProgress: true
          }
        })
      }
    }
  }
}

/**
 * Add an alert to the cache if the item matches an audit course that is withdrawn.
 *
 * @param cache Apollo Client Cache
 * @param item Item to check for WithdrawnCourse alert
 * @param courses Array of Audit Courses
 */
function detectAndAddWithdrawnCourseAlert<T>(
  cache: ApolloCache<T>,
  item: BoardItemFragment | null | undefined,
  courses: AuditCourse[]
) {
  // Item must have courseNumber and subjectId to match an audit course
  if (item?.courseNumber && item?.subjectId) {
    const matchingAuditCourses = getMatchingAuditCourses(courses, item)

    // Now find out if any of the matching records are withdrawn and add the alert
    if (matchingAuditCourses?.length) {
      const withdrawnAuditCourse = matchingAuditCourses.find((c) => c.withdrawn)
      if (withdrawnAuditCourse) {
        const newAlert: BoardAlertFragment = {
          __typename: 'PlanAlert',
          alertType: AlertType.WithdrawnCourse,
          alertKey: `${AlertType.WithdrawnCourse}-ITEM_${item.id}_${item.groupId}`,
          level: AlertLevel.Warning,
          groupId: item.groupId,
          itemId: item.id
        }
        addPlanAlertToCache(cache, newAlert, item)
      }
    }
  }
}

/**
 * Update the cache when a new item is added.
 *
 * @param cache Apollo Client Cache
 * @param group Plan Group
 * @param newItem Item that is being added to the group
 * @param ownerAppUserId App user ID of the plan owner
 * @returns true when the catch/evict fallback fired — the caller should force a
 * refetch of the plan (useRefetchPlanRecovery), because the evicted group only
 * re-renders after a successful automatic refetch, which is not guaranteed
 * under auth/timeout blips (PLAN-1084).
 */
export function updateAddPlanItemCache<T>(
  cache: ApolloCache<T>,
  group: BoardGroupFragment,
  newItem: PlanItem | null | undefined,
  ownerAppUserId: number,
  enableCrossListingUsedCourses?: boolean
): boolean {
  if (!newItem) {
    return false
  }
  try {
    cache.modify({
      id: cache.identify(group),
      fields: {
        items(existingItems: Reference[] = []) {
          const newItemRef = cache.writeFragment({
            data: newItem,
            fragment: BoardItemFragmentDoc,
            fragmentName: 'boardItem'
          })
          return [...existingItems, newItemRef]
        }
      }
    })
    const auditCourses = getAuditCourses(cache, ownerAppUserId)

    detectAndAddDuplicateCourseAlerts(cache, newItem, group.planId)
    detectAndAddInactiveCourseAlert(cache, newItem)
    detectAndAddRequisiteAlert(cache, newItem)
    detectAndAddUnavailableAlert(cache, newItem)
    detectAndAddUsedToMeetRequirementAlert(
      cache,
      newItem,
      auditCourses,
      enableCrossListingUsedCourses
    )
    detectAndAddNotUsedToMeetRequirementAlert(cache, newItem, auditCourses)
    detectAndAddInProgressCourseAlert(cache, newItem, auditCourses)
    detectAndAddEnrolledCourseAlert(cache, newItem, auditCourses)
    detectAndAddWithdrawnCourseAlert(cache, newItem, auditCourses)
    return false
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[updateAddPlanItemCache] Cache update failed, skipping:', error)
    // This catch fires on a SUCCESSFUL add mutation when the cache write / alert
    // recalc throws. Without it the error propagates out of the mutation `update`,
    // the added card never renders, and the advisor re-adds the course — which is
    // how duplicate rows appear (PLAN-1084). Do not rethrow — the item is already
    // persisted; evict the group so Apollo refetches it and the card appears.
    Sentry.captureException(error, {
      tags: { feature: 'plan-add-cache' },
      extra: {
        groupId: group.id,
        planId: group.planId,
        itemId: newItem.id
      }
    })
    cache.evict({ id: cache.identify(group) })
    cache.gc()
    return true
  }
}

/**
 * Detects and adds audit-related Plan alerts.
 * According to the current state of the cache.
 * Useful after the auditReport cache has been updated,
 * either manually or on subscription data.
 *
 * @param cache Apollo Cache
 * @param planId
 */
export function updateAuditPlanAlertsCache<T>(
  cache: ApolloCache<T>,
  planId: string,
  enableCrossListingUsedCourses?: boolean
) {
  const planQuery = cache.readQuery<PlanQuery>({
    query: PlanDocument,
    variables: { id: planId }
  })
  if (planQuery?.plan) {
    const { ownerAppUserId } = planQuery.plan
    const auditCourses = getAuditCourses(cache, ownerAppUserId)
    planQuery.plan.groups.forEach((group) => {
      group.items.forEach((planItem) => {
        // Clear out the existing audit related alerts
        detectAndRemoveAlert(cache, AlertType.ItemMeetsRequirement, planItem)
        detectAndRemoveAlert(cache, AlertType.ItemDoesNotMeetRequirement, planItem)
        detectAndRemoveAlert(cache, AlertType.InProgressCourse, planItem)
        detectAndRemoveAlert(cache, AlertType.EnrolledCourse, planItem)
        detectAndRemoveAlert(cache, AlertType.WithdrawnCourse, planItem)

        // Re-calculate the alerts
        detectAndAddUsedToMeetRequirementAlert(
          cache,
          planItem,
          auditCourses,
          enableCrossListingUsedCourses
        )
        detectAndAddNotUsedToMeetRequirementAlert(cache, planItem, auditCourses)
        detectAndAddInProgressCourseAlert(cache, planItem, auditCourses)
        detectAndAddEnrolledCourseAlert(cache, planItem, auditCourses)
        detectAndAddWithdrawnCourseAlert(cache, planItem, auditCourses)
      })
    })
  }
}

/**
 * Changes locked value for planItems cache
 * after updating it with the lock button in the plan title
 *
 * @param cache Apollo Cache
 * @param planId planId
 * @param locked the lock value selected
 *
 */
export function updateLockForAllPlanItemsCache<T>(
  cache: ApolloCache<T>,
  planId: string,
  locked: boolean
) {
  const planQuery = cache.readQuery<PlanQuery>({
    query: PlanDocument,
    variables: { id: planId }
  })
  if (planQuery?.plan) {
    planQuery.plan.groups.forEach((group) => {
      group.items.forEach((planItem) => {
        // Retrieve the cacheItem
        const cacheItem = cache.readFragment<BoardItemFragment>({
          id: `PlanItem:${planItem.id}`,
          fragment: BoardItemFragmentDoc,
          fragmentName: 'boardItem'
        })

        // Set its locked value
        cache.writeFragment({
          id: `PlanItem:${planItem.id}`,
          fragment: BoardItemFragmentDoc,
          fragmentName: 'boardItem',
          data: {
            ...cacheItem,
            locked
          }
        })
      })
    })
  }
}

/**
 * For the deleted plan item look for other duplicate courses
 * and remove DUPLICATE_ALERT if there is only one duplicate remaining
 * @param cache The Apollo Cache.
 * @param courses All courses to be re-scanned for duplicates.
 * @param planItem The particular item to remove the alert from.
 */
function detectAndRemoveDuplicateCourseAlerts<T>(
  cache: ApolloCache<T>,
  courses: Array<BoardItemFragment>,
  planItem: BoardItemFragment | PlanItemDetailsFragment
) {
  // remove DUPLICATE_COURSE alert for current course
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      planAlerts(existingAlerts: Reference[], { readField }) {
        const alerts = existingAlerts || []
        return alerts.filter(
          (ref) =>
            !(
              AlertType.DuplicateCourse === readField('alertType', ref) &&
              planItem.id === readField('itemId', ref)
            )
        )
      }
    }
  })

  // find any duplicate courses and remove the DUPLICATE_COURSE alert
  const dupeCourses = courses.filter(
    (c) => c.subjectId === planItem.subjectId && c.courseNumber === planItem.courseNumber
  )

  // remove other DUPLICATE_COURSE alerts if there is only one dupe remaining
  if (dupeCourses.length === 1) {
    const dupeCourse = dupeCourses[0]

    cache.modify({
      id: 'ROOT_QUERY',
      fields: {
        planAlerts(existingAlerts: Reference[], { readField }) {
          const alerts = existingAlerts || []
          // get the alert key
          const alertRef = alerts.find(
            (ref) =>
              AlertType.DuplicateCourse === readField('alertType', ref) &&
              dupeCourse.id === readField('itemId', ref)
          )
          const alertKey = readField('alertKey', alertRef)

          // filtering by alertKey will remove all alerts attached to dupe courses
          return alerts.filter((ref) => alertKey !== readField('alertKey', ref))
        }
      }
    })
  }
}

/**
 * Remove all alerts for a particular type from an item
 * @param cache The Apollo Cache.
 * @param alertType The type of alert to remove from the item.
 * @param planItem The particular item to remove the alert from.
 */
function detectAndRemoveAlert<T>(
  cache: ApolloCache<T>,
  alertType: AlertType,
  planItem: BoardItemFragment | PlanItemDetailsFragment
) {
  // remove alert for current course
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      planAlerts(existingAlerts: Reference[], { readField }) {
        const alerts = existingAlerts || []
        return alerts.filter(
          (ref) =>
            !(alertType === readField('alertType', ref) && planItem.id === readField('itemId', ref))
        )
      }
    }
  })
}

export function updateDeletePlanItemCache<T>(
  cache: ApolloCache<T>,
  planItem: PlanItemDetailsFragment | undefined,
  data: DeletePlanItemMutation | null | undefined
) {
  if (planItem && data?.deletePlanItem.success) {
    cache.modify({
      id: `PlanGroup:${planItem.groupId}`,
      fields: {
        items(existingItems: Reference[], { readField }) {
          const items = existingItems || []
          return items.filter((itemRef) => planItem.id !== readField('id', itemRef))
        }
      }
    })

    const allCourseItems = getAllCourseItems(cache, planItem.planId)
    detectAndRemoveDuplicateCourseAlerts(cache, allCourseItems, planItem)
    detectAndRemoveAlert(cache, AlertType.InactiveCourse, planItem)
    detectAndRemoveAlert(cache, AlertType.ItemMeetsRequirement, planItem)
    detectAndRemoveAlert(cache, AlertType.ItemDoesNotMeetRequirement, planItem)
    detectAndRemoveAlert(cache, AlertType.InProgressCourse, planItem)
    detectAndRemoveAlert(cache, AlertType.Requisite, planItem)
    detectAndRemoveAlert(cache, AlertType.Unavailable, planItem)
    detectAndRemoveAlert(cache, AlertType.EnrolledCourse, planItem)
  }
}

export function updateDeletePlanGroupCache<T>(cache: ApolloCache<T>, groupId: string) {
  const cacheGroup = cache.readFragment<BoardGroupFragment>({
    id: `PlanGroup:${groupId}`,
    fragment: BoardGroupFragmentDoc,
    fragmentName: 'boardGroup'
  })

  if (cacheGroup) {
    const groupIds: Array<string> = []

    cache.modify({
      id: `Plan:${cacheGroup.planId}`,
      fields: {
        groups(groups: Reference[], { readField }) {
          return groups.filter((group) => {
            const curId = readField('id', group)
            if (curId && curId !== groupId) {
              // track all the group ids to update the titles individually
              groupIds.push(curId.toString())
            }
            // remove deleted group
            return curId !== groupId
          })
        }
      }
    })

    // remove the group from planGroups
    cache.modify({
      id: 'ROOT_QUERY',
      fields: {
        planGroups(existingGroups: Reference[], { readField }) {
          const groups = existingGroups || []
          return groups.filter((ref) => groupId !== readField('id', ref))
        }
      }
    })

    const allCourseItems = getAllCourseItems(cache, cacheGroup.planId)

    cacheGroup.items.forEach((item) => {
      detectAndRemoveDuplicateCourseAlerts(cache, allCourseItems, item)
    })
  }
}

export function updateAddPlanGroupCache<T>(
  cache: ApolloCache<T>,
  plan: BoardPlanFragment,
  group: BoardGroupFragment | undefined
) {
  if (group) {
    const newGroupRef = cache.writeFragment({
      data: group,
      fragment: BoardGroupFragmentDoc,
      fragmentName: 'boardGroup'
    })

    cache.modify({
      id: cache.identify(plan),
      fields: {
        groups(existingGroups: Reference[] = []) {
          return [...existingGroups, newGroupRef]
        }
      }
    })

    cache.modify({
      id: 'ROOT_QUERY',
      fields: {
        planGroups(existingGroups: Reference[]) {
          return [...existingGroups, newGroupRef]
        }
      }
    })
  }
}

export function updateMovePlanGroupCache<T>(
  cache: ApolloCache<T>,
  planId: String,
  group: BoardGroupFragment | undefined,
  groups: BoardGroupFragment[]
) {
  if (group) {
    // note: this Fragment represents the updated PlanGroup
    const planGroupFragment = cache.readFragment<BoardGroupFragment>({
      id: `PlanGroup:${group.id}`,
      fragment: BoardGroupFragmentDoc,
      fragmentName: 'boardGroup'
    })
    // get data from cache
    const planFragment = cache.readFragment<BoardPlanFragment>({
      id: `Plan:${planId}`,
      fragment: BoardPlanFragmentDoc,
      fragmentName: 'boardPlan'
    })

    if (planFragment) {
      const previousPosition = groups.find((i) => i.id === group.id)?.displaySequence

      if (planGroupFragment && previousPosition) {
        const newSequence = changeItemSequence(
          planGroupFragment,
          planFragment.groups,
          previousPosition
        )
        const cachePlan = {
          ...planFragment,
          groups: newSequence
        }
        cache.writeFragment({
          id: `Plan:${planId}`,
          fragment: BoardPlanFragmentDoc,
          fragmentName: 'boardPlan',
          data: cachePlan
        })
      }
    }
  }
}

function moveRelatedAlerts<T>(
  cache: ApolloCache<T>,
  itemId: string,
  destinationGroupId: string | undefined | null
) {
  if (destinationGroupId) {
    // we're not actually modifying anything here
    // we're using cache.modify as a workaround to find all effect alerts
    const relatedAlertIds: Array<string> = []
    cache.modify({
      id: 'ROOT_QUERY',
      fields: {
        planAlerts(existingAlerts: Reference[], { readField }) {
          const alerts = existingAlerts || []
          // get the alert key
          const alertRefs = alerts
            .filter((ref) => itemId.toString() === readField('itemId', ref))
            // eslint-disable-next-line no-underscore-dangle
            .map((ref) => ref.__ref)

          if (alertRefs) {
            // eslint-disable-next-line no-underscore-dangle
            relatedAlertIds.push(...alertRefs)
          }

          // return all existingAlerts because we do not want to modify anything here
          return alerts
        }
      }
    })

    // change the groupId field for all related alerts
    relatedAlertIds.forEach((alertRef) => {
      cache.modify({
        id: alertRef,
        fields: {
          groupId() {
            return destinationGroupId
          }
        }
      })
    })
  }
}

/**
 * Update the Cache when moving an item.
 * This not only updates which group an item is in, but also re-calculates display sequences.
 * In addition, we need to update any groupIds of related alerts.
 *
 * @returns true when the catch/evict fallback fired — the caller should force a
 * refetch of the plan, because the evicted groups only re-render after a successful
 * automatic refetch, which is not guaranteed under auth/timeout blips (PLAN-1084).
 */
export function updateMovePlanItemCache<T>(
  cache: ApolloCache<T>,
  data: MovePlanItemMutation | null | undefined,
  sourceGroupId: string,
  previousPosition: number
): boolean {
  if (data?.movePlanItem) {
    try {
      // get data from mutation result
      const planItem = data.movePlanItem
      const itemId = planItem.id
      const destinationGroupId = planItem.groupId
      // note: this Fragment represents the updated PlanItem
      const planItemFragment = cache.readFragment<BoardItemFragment>({
        id: `PlanItem:${itemId}`,
        fragment: BoardItemFragmentDoc,
        fragmentName: 'boardItem'
      })
      const sourceGroup = cache.readFragment<BoardGroupFragment>({
        id: `PlanGroup:${sourceGroupId}`,
        fragment: BoardGroupFragmentDoc,
        fragmentName: 'boardGroup'
      })
      const destGroup = cache.readFragment<BoardGroupFragment>({
        id: `PlanGroup:${destinationGroupId}`,
        fragment: BoardGroupFragmentDoc,
        fragmentName: 'boardGroup'
      })
      const planQuery = cache.readQuery<PlanQuery>({
        query: PlanDocument,
        variables: { id: destGroup?.planId }
      })
      const ownerAppUserId = planQuery?.plan.ownerAppUserId

      if (planItemFragment && sourceGroup && destGroup) {
        if (destinationGroupId === sourceGroupId) {
          // move within the same group, different sequence
          const newCachedGroup = {
            ...destGroup,
            items: changeItemSequence(planItemFragment, destGroup.items, previousPosition)
          }

          cache.writeFragment({
            id: `PlanGroup:${destGroup.id}`,
            fragment: BoardGroupFragmentDoc,
            fragmentName: 'boardGroup',
            data: newCachedGroup
          })
        } else {
          // move item to a different group
          const newSourceGroup = {
            ...sourceGroup,
            items: removeItemFromList(planItemFragment, sourceGroup.items, previousPosition)
          }
          const newDestGroup = {
            ...destGroup,
            items: addItemToList(planItemFragment, destGroup.items)
          }

          cache.writeFragment({
            id: `PlanGroup:${sourceGroup.id}`,
            fragment: BoardGroupFragmentDoc,
            fragmentName: 'boardGroup',
            data: newSourceGroup
          })
          cache.writeFragment({
            id: `PlanGroup:${destGroup.id}`,
            fragment: BoardGroupFragmentDoc,
            fragmentName: 'boardGroup',
            data: newDestGroup
          })
        }
      }

      // remove this alert every time because we need to re-calculate for a new term
      detectAndRemoveAlert(cache, AlertType.Unavailable, planItem)
      detectAndRemoveAlert(cache, AlertType.InactiveCourse, planItem)
      detectAndRemoveAlert(cache, AlertType.Requisite, planItem)

      moveRelatedAlerts(cache, itemId, destinationGroupId)

      if (planItem.subjectId && planItem.courseNumber) {
        detectAndAddInactiveCourseAlert(cache, planItem)
        detectAndAddUnavailableAlert(cache, planItem)
        detectAndAddRequisiteAlert(cache, planItem)
        if (ownerAppUserId) {
          const auditCourses = getAuditCourses(cache, ownerAppUserId)
          detectAndAddUsedToMeetRequirementAlert(cache, planItem, auditCourses)
          detectAndAddNotUsedToMeetRequirementAlert(cache, planItem, auditCourses)
          detectAndAddInProgressCourseAlert(cache, planItem, auditCourses)
          detectAndAddEnrolledCourseAlert(cache, planItem, auditCourses)
          detectAndAddWithdrawnCourseAlert(cache, planItem, auditCourses)
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[updateMovePlanItemCache] Cache update failed, skipping:', error)
      // Diagnostic logging: this catch fires on a SUCCESSFUL move mutation when the
      // cache update / alert / audit recalc throws — it then evicts both groups, producing the
      // silent revert with NO toast. console.error is invisible in production, so also report
      // to Sentry to measure how often this silent path fires and on which groups. Behavior is
      // unchanged — we still evict + gc below and do not rethrow.
      Sentry.captureException(error, {
        tags: { feature: 'plan-move-cache' },
        extra: {
          sourceGroupId,
          destGroupId: data?.movePlanItem?.groupId,
          itemId: data?.movePlanItem?.id
        }
      })
      // Do not re-throw — mutation completed successfully on the server.
      // Evict stale cache entries to force Apollo to refetch on next render.
      cache.evict({ id: `PlanGroup:${sourceGroupId}` })
      if (data?.movePlanItem?.groupId) {
        cache.evict({ id: `PlanGroup:${data.movePlanItem.groupId}` })
      }
      cache.gc()
      return true
    }
  }
  return false
}
/**
 * Update the Cache when editing an item
 * Apollo naturally handles most of the updates to an item.
 * But we need to handle when the group changes for an item.
 */
export function updateUpdatePlanItemCache<T>(
  cache: ApolloCache<T>,
  data: UpdatePlanItemMutation | null | undefined,
  previousPlanItem: BoardItemFragment | null | undefined,
  ownerAppUserId: number | null | undefined
) {
  if (previousPlanItem && data?.updatePlanItem) {
    const planItem = data.updatePlanItem

    if (
      previousPlanItem.subjectId !== planItem.subjectId ||
      previousPlanItem.courseNumber !== planItem.courseNumber
    ) {
      // remove duplicatecourse alerts based on the previousPlanItem because it matches on the subject and course number
      // and removes the alerts for other items with the same subject/course number
      const allCourseItems = getAllCourseItems(cache, planItem.planId)
      detectAndRemoveDuplicateCourseAlerts(cache, allCourseItems, previousPlanItem)

      detectAndRemoveAlert(cache, AlertType.InactiveCourse, planItem)
      detectAndRemoveAlert(cache, AlertType.InProgressCourse, planItem)
      detectAndRemoveAlert(cache, AlertType.ItemDoesNotMeetRequirement, planItem)
      detectAndRemoveAlert(cache, AlertType.ItemMeetsRequirement, planItem)
      detectAndRemoveAlert(cache, AlertType.Requisite, planItem)
      detectAndRemoveAlert(cache, AlertType.Unavailable, planItem)

      // Course is being selected for a placeholder,
      // so we need to look for any duplicates and check conditions with actual course data
      if (planItem.subjectId && planItem.courseNumber) {
        const auditCourses = getAuditCourses(cache, ownerAppUserId || 0)
        detectAndAddDuplicateCourseAlerts(cache, planItem, planItem.planId)
        detectAndAddInactiveCourseAlert(cache, planItem)
        detectAndAddRequisiteAlert(cache, planItem)
        detectAndAddUnavailableAlert(cache, planItem)
        detectAndAddUsedToMeetRequirementAlert(cache, planItem, auditCourses)
        detectAndAddNotUsedToMeetRequirementAlert(cache, planItem, auditCourses)
        detectAndAddInProgressCourseAlert(cache, planItem, auditCourses)
        detectAndAddEnrolledCourseAlert(cache, planItem, auditCourses)
        detectAndAddWithdrawnCourseAlert(cache, planItem, auditCourses)
      }
    }

    if (previousPlanItem.groupId !== planItem.groupId) {
      // remove item from the previous group
      cache.modify({
        id: `PlanGroup:${previousPlanItem.groupId}`,
        fields: {
          items(existingItems: Reference[], { readField }) {
            const items = existingItems || []
            return items.filter((itemRef) => planItem.id !== readField('id', itemRef))
          }
        }
      })

      // add item to the destination group
      cache.modify({
        id: `PlanGroup:${planItem.groupId}`,
        fields: {
          items(existingItems: Reference[] = []) {
            const newItemRef = cache.writeFragment({
              data: planItem,
              fragment: BoardItemFragmentDoc,
              fragmentName: 'boardItem'
            })
            return [...existingItems, newItemRef]
          }
        }
      })

      moveRelatedAlerts(cache, planItem.id, planItem.groupId)
    }
  }
}

/**
 * Updates the comments under all the plans that are in cache
 *
 * @param cache Apollo Cache
 * @param planIds List of planIds updated
 */
export function updateAllPlanCommentsCache<T>(cache: ApolloCache<T>, planIds: Array<string>) {
  planIds.forEach((planId) => {
    const planQuery = cache.readQuery<PlanQuery>({
      query: PlanDocument,
      variables: { id: planId }
    })
    if (planQuery?.plan) {
      planQuery.plan.groups.forEach((group) => {
        group.comments.forEach((comment) => {
          const commentFragment = cache.readFragment<BoardCommentFragment>({
            id: `Comment:${comment.id}`,
            fragment: BoardCommentFragmentDoc,
            fragmentName: 'boardComment'
          })
          cache.writeFragment({
            id: `Comment:${comment.id}`,
            fragment: BoardCommentFragmentDoc,
            fragmentName: 'boardComment',
            data: {
              ...commentFragment,
              read: true
            }
          })
        })
      })
    }
  })
}

export default updateOwnerPlansCache
